import { useState, useEffect } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { useSelector } from '../../context/hooks';
import * as selectors from '../../context/selectors';
import { formatNumber } from '../strings';

import { useDisableWheel } from './use-disable-wheel';

export type NumericInputProps = {
	id: string;
	/** The numeric value controlled by the parent. `undefined` renders a blank field. */
	value: number | undefined;
	/** Called with the new numeric value whenever the input changes. When the field
	 *  is cleared the parent receives `undefined` and the input displays a blank field. */
	onChange: (value: number | undefined) => void;
	min?: number;
	max?: number;
	placeholder?: string;
	/**
	 * `'field'` (default) renders a standalone field.
	 * `'inline'` renders a pill styled to sit alongside other controls.
	 */
	variant?: 'field' | 'inline';
	/** Overrides the input width (in px) so each screen can size it independently. */
	width?: number;
	/** Renders the error border treatment (thicker red border) when true. */
	invalid?: boolean;
	/** Called on blur with the input's current value (`undefined` when blank). */
	onBlur?: (value: number | undefined) => void;
	'aria-label'?: string;
	'aria-labelledby'?: string;
	'aria-required'?: boolean;
};

/**
 * Presentational numeric input that:
 * - Accepts only whole non-negative numbers and displays them with locale
 *   thousands separators (e.g. `145,000`), stripping the separators on parse.
 * - Shows a blank field when the value is `0`/`undefined`, reporting `undefined` when cleared.
 * - Stops the scroll-wheel and the up/down arrow keys from changing the value
 *   (without blocking page scroll) and hides the native number spinners.
 * - Clamps typed values to `min`/`max` when provided.
 */
export const NumericInput = (props: NumericInputProps) => {
	const {
		id,
		value,
		onChange,
		min,
		max,
		placeholder,
		variant = 'field',
		width,
		invalid,
		onBlur,
		'aria-label': ariaLabel,
		'aria-labelledby': ariaLabelledby,
		'aria-required': ariaRequired,
	} = props;

	const locale = useSelector(selectors.getNumberFormatLocale);

	const toDisplayString = (v: number | undefined): string =>
		v === undefined || v === 0 ? '' : formatNumber(v, locale);

	const inputRef = useDisableWheel<HTMLInputElement>();

	const [displayValue, setDisplayValue] = useState<string>(toDisplayString(value));

	// Sync display value when the parent value changes externally (e.g. a reset).
	useEffect(() => {
		setDisplayValue(toDisplayString(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		// Strip the thousands separators (and any other non-digits) before parsing.
		const digits = e.target.value.replace(/\D/g, '');

		if (digits === '') {
			setDisplayValue('');
			onChange(undefined);
			return;
		}

		const parsed = parseInt(digits, 10);
		if (isNaN(parsed)) {
			return;
		}

		const clamped =
			min !== undefined && max !== undefined
				? Math.min(max, Math.max(min, parsed))
				: min !== undefined
					? Math.max(min, parsed)
					: max !== undefined
						? Math.min(max, parsed)
						: parsed;

		// Re-format with thousands separators (also drops any leading zeros).
		setDisplayValue(formatNumber(clamped, locale));
		onChange(clamped);
	};

	const handleBlur = () => {
		const digits = displayValue.replace(/\D/g, '');
		onBlur?.(digits === '' ? undefined : parseInt(digits, 10));
	};

	// Stop the arrow keys from stepping the value, mirroring the scroll-wheel.
	// Block '-', 'e', and '.' so only whole non-negative numbers can be typed.
	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
			e.preventDefault();
		}
		if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '.') {
			e.preventDefault();
		}
	};

	// Dynamic width override; applied only when provided so the variant's
	// default width still wins when `width` is omitted.
	const widthStyles = css({ width });

	return (
		<input
			ref={inputRef}
			id={id}
			type="text"
			inputMode="numeric"
			css={[
				baseInputStyles,
				variant === 'inline' ? inlineVariantStyles : fieldVariantStyles,
				width !== undefined && widthStyles,
				invalid && invalidStyles,
			]}
			aria-invalid={invalid || undefined}
			value={displayValue}
			placeholder={placeholder}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledby}
			aria-required={ariaRequired}
			onChange={handleChange}
			onBlur={onBlur ? handleBlur : undefined}
			onKeyDown={handleKeyDown}
			autoComplete="off"
		/>
	);
};

const baseInputStyles = css({
	appearance: 'none',
	MozAppearance: 'textfield',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	paddingBlock: token('space.100'),
	paddingInline: token('space.200'),
	// Hide the native number spinners so the scroll-wheel affordance disappears.
	'::-webkit-inner-spin-button': {
		WebkitAppearance: 'none',
		margin: 0,
	},
	'::-webkit-outer-spin-button': {
		WebkitAppearance: 'none',
		margin: 0,
	},
});

const fieldVariantStyles = css({
	width: 160,
	height: 40,
	fontSize: '1rem',
	lineHeight: '24px',
	borderRadius: 20,
	color: wacToken('color.neutral.light1100'),
});

const inlineVariantStyles = css({
	fontSize: 'inherit',
	color: wacToken('color.primary.black'),
	borderRadius: wacToken('corner.radius.pill'),
});

const invalidStyles = css({
	borderWidth: '2px',
	borderColor: wacToken('color.secondary.red'),
	'&:focus': {
		borderWidth: '3px',
		outline: 0,
	},
});
